import React, { FC, useEffect } from 'react';
import {
    TextField, FormControl,
    InputLabel, Select, MenuItem,
    FormControlLabel, Checkbox,
    Radio, RadioGroup, Typography, Grid,
    Autocomplete
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ApplicationEnum } from 'repository/timekeeper/@types/TypeAplicationEnum';
import { FrequencyUnitTime } from 'repository/timekeeper/@types/TypeFrequencyUnitTime';
import { DatePattern } from 'repository/timekeeper/@types/DatePattern';
import { Paycode } from 'repository/timekeeper/@types/PaycodeModel';
import { CalificadorCreate } from 'repository/timekeeper/@types/Calificador';
import { TipoOrtorgarConcesion } from 'repository/timekeeper/@types/TypeGiveGrant';
import { QualifiersList } from './QualifiersList';
import { GrantPercibidoCreateSchema } from 'schemas/Grants';

export interface GrantFormPercibidoData {
    basedOn: TipoOrtorgarConcesion.INMEDIATAMENTE | TipoOrtorgarConcesion.PATRON_DE_FECHAS;
    paycodeId: number | '';
    datePatternId: number | '';
    applicationMode: ApplicationEnum;
    applicationModeAmount: number;
    applicationModeMove: boolean;
    applicationModeDatePatternId: number | '';
    expiration: boolean;
    expirationNumber: number;
    expirationUnit: FrequencyUnitTime;
    qualifiers: CalificadorCreate[];
}

interface GrantFormPercibidoProps {
    formData: GrantFormPercibidoData;
    setFormData: (data: GrantFormPercibidoData) => void;
    paycodes: Paycode[];
    datePatterns: DatePattern[];
}

export const GrantFormPercibido: FC<GrantFormPercibidoProps> = ({
    formData,
    setFormData,
    paycodes,
    datePatterns
}) => {
    const { t } = useTranslation();
    const [expirationError, setExpirationError] = React.useState<string | undefined>();

    useEffect(() => {
        if (paycodes.length > 0 && formData.paycodeId === '') {
            setFormData({ ...formData, paycodeId: paycodes[0].paycode_id });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paycodes, formData.paycodeId]);

    return (
        <Grid container spacing={2}>

            <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" gutterBottom>
                    {t('accruals.grants.dialog.fields.base_concession_on_accumulated_amount_in_payment_code')}
                </Typography>
                <FormControl fullWidth>
                    {/* <InputLabel>{t('accruals.grants.dialog.fields.paycode')}</InputLabel>
                    <Select
                        label={t('accruals.grants.dialog.fields.paycode')}
                        value={formData.paycodeId}
                        onChange={(e) => setFormData({ ...formData, paycodeId: e.target.value as number })}
                        required
                    >
                        {paycodes.map((paycode) => (
                            <MenuItem key={paycode.paycode_id} value={paycode.paycode_id}>
                                {paycode.name}
                            </MenuItem>
                        ))}
                    </Select> */}
                    <Autocomplete
                        options={paycodes}
                        getOptionLabel={(option) => option.name}
                        value={paycodes.find((p) => p.paycode_id === formData.paycodeId) || null}
                        onChange={(event, newValue) => {
                            setFormData({ ...formData, paycodeId: newValue ? newValue.paycode_id : '' });
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t('accruals.grants.dialog.fields.paycode')}
                                required
                            />
                        )}
                    />
                </FormControl>
            </Grid>

            <Grid size={{ xs: 12 }}>
                <RadioGroup
                    value={formData.basedOn}
                    onChange={(e) => setFormData({ ...formData, basedOn: e.target.value as TipoOrtorgarConcesion.INMEDIATAMENTE | TipoOrtorgarConcesion.PATRON_DE_FECHAS })}
                >
                    <FormControlLabel
                        value={TipoOrtorgarConcesion.INMEDIATAMENTE}
                        control={<Radio />}
                        label={t('accruals.grants.dialog.fields.immediately')}
                    />
                    <FormControlLabel
                        value={TipoOrtorgarConcesion.PATRON_DE_FECHAS}
                        control={<Radio />}
                        label={t('accruals.grants.dialog.fields.date_pattern')}
                    />
                </RadioGroup>
            </Grid>

            {formData.basedOn === TipoOrtorgarConcesion.PATRON_DE_FECHAS && (
                <>
                    <Grid size={{ xs: 12 }}>
                        <FormControl fullWidth>
                            {/* <InputLabel>{t('accruals.grants.dialog.fields.date_pattern')}</InputLabel>
                            <Select
                                label={t('accruals.grants.dialog.fields.date_pattern')}
                                value={formData.datePatternId}
                                onChange={(e) => setFormData({ ...formData, datePatternId: e.target.value as number })}
                            >
                                {datePatterns.map((pattern) => (
                                    <MenuItem key={(pattern as any).date_pattern_id ?? (pattern as any).id} value={(pattern as any).date_pattern_id ?? (pattern as any).id}>
                                        {(pattern as any).name}
                                    </MenuItem>
                                ))}
                            </Select> */}
                            <Autocomplete
                                options={datePatterns}
                                getOptionLabel={(option) => (option as any).name}
                                value={datePatterns.find((d) => (d as any).date_pattern_id === formData.datePatternId || (d as any).id === formData.datePatternId) || null}
                                onChange={(event, newValue) => {
                                    setFormData({ ...formData, datePatternId: newValue ? ((newValue as any).date_pattern_id ?? (newValue as any).id) : '' });
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t('accruals.grants.dialog.fields.date_pattern')}
                                    />
                                )}
                            />
                        </FormControl>
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            {t('accruals.grants.dialog.fields.give_grant')}
                        </Typography>
                        <RadioGroup
                            value={formData.applicationMode}
                            onChange={(e) => setFormData({ ...formData, applicationMode: e.target.value as ApplicationEnum })}
                        >
                            <FormControlLabel
                                value={ApplicationEnum.USAR_CANTIDAD_CONCESION}
                                control={<Radio />}
                                label={t('accruals.grants.dialog.fields.use_amount_of_concession_received')}
                            />
                            <FormControlLabel
                                value={ApplicationEnum.ACOTAR_EN_INCREMENTO}
                                control={<Radio />}
                                label={t('accruals.grants.dialog.fields.limit_concession_in_increments')}
                            />
                        </RadioGroup>
                    </Grid>

                    {formData.applicationMode === ApplicationEnum.ACOTAR_EN_INCREMENTO && (
                        <>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label={t('accruals.grants.dialog.fields.increment')}
                                    slotProps={{
                                        htmlInput: { min: 0 }
                                    }}
                                    value={formData.applicationModeAmount}
                                    onChange={(e) => setFormData({ ...formData, applicationModeAmount: Number(e.target.value) })}
                                />
                            </Grid>

                            <Grid size={{ xs: 12 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formData.applicationModeMove}
                                            onChange={(e) => setFormData({ ...formData, applicationModeMove: e.target.checked })}
                                        />
                                    }
                                    label={t('accruals.grants.dialog.fields.shift_concession_hours_and_reset')}
                                />
                            </Grid>

                            {formData.applicationModeMove && (
                                <Grid size={{ xs: 12 }}>
                                    <FormControl fullWidth>
                                        {/* <InputLabel>{t('accruals.grants.dialog.fields.date_pattern')}</InputLabel>
                                        <Select
                                            label={t('accruals.grants.dialog.fields.date_pattern')}
                                            value={formData.applicationModeDatePatternId}
                                            onChange={(e) => setFormData({ ...formData, applicationModeDatePatternId: e.target.value as number })}
                                        >
                                            {datePatterns.map((pattern) => (
                                                <MenuItem key={(pattern as any).date_pattern_id ?? (pattern as any).id} value={(pattern as any).date_pattern_id ?? (pattern as any).id}>
                                                    {(pattern as any).name}
                                                </MenuItem>
                                            ))}
                                        </Select> */}
                                        <Autocomplete
                                            options={datePatterns}
                                            getOptionLabel={(option) => (option as any).name}
                                            value={datePatterns.find((d) => (d as any).date_pattern_id === formData.applicationModeDatePatternId || (d as any).id === formData.applicationModeDatePatternId) || null}
                                            onChange={(event, newValue) => {
                                                setFormData({ ...formData, applicationModeDatePatternId: newValue ? ((newValue as any).date_pattern_id ?? (newValue as any).id) : '' });
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label={t('accruals.grants.dialog.fields.date_pattern')}
                                                />
                                            )}
                                        />
                                    </FormControl>
                                </Grid>
                            )}
                        </>
                    )}
                </>
            )}

            <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" gutterBottom>
                    {t('accruals.grants.dialog.fields.expiration')}
                </Typography>
                <RadioGroup
                    value={formData.expiration}
                    onChange={(e) => setFormData({ ...formData, expiration: e.target.value === 'true' })}
                >
                    <FormControlLabel
                        value={false}
                        control={<Radio />}
                        label={t('accruals.grants.dialog.fields.does_not_expire')}
                    />
                    <FormControlLabel
                        value={true}
                        control={<Radio />}
                        label={t('accruals.grants.dialog.fields.expires')}
                    />
                </RadioGroup>
            </Grid>

            {formData.expiration && (
                <Grid size={{ xs: 12 }}>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                type="number"
                                label={t('accruals.grants.dialog.fields.expires')}
                                slotProps={{
                                    htmlInput: { min: 0 }
                                }}
                                value={formData.expirationNumber}
                                onChange={(e) => {
                                    const value = Number(e.target.value);
                                    setFormData({ ...formData, expirationNumber: value });
                                    // Validar expiration_number
                                    const result = GrantPercibidoCreateSchema.shape.expiration_number.safeParse(value);
                                    setExpirationError(result.success ? undefined : result.error.issues[0]?.message);
                                }}
                                error={!!expirationError}
                                helperText={expirationError}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth>
                                <InputLabel>{t('accruals.grants.dialog.fields.unit')}</InputLabel>
                                <Select
                                    label={t('accruals.grants.dialog.fields.unit')}
                                    value={formData.expirationUnit}
                                    onChange={(e) => setFormData({ ...formData, expirationUnit: e.target.value as FrequencyUnitTime })}
                                >
                                    <MenuItem value={FrequencyUnitTime.DIAS}>{t('accruals.grants.dialog.fields.unit_options.DAYS')}</MenuItem>
                                    <MenuItem value={FrequencyUnitTime.SEMANAS}>{t('accruals.grants.dialog.fields.unit_options.WEEKS')}</MenuItem>
                                    <MenuItem value={FrequencyUnitTime.MESES}>{t('accruals.grants.dialog.fields.unit_options.MONTHS')}</MenuItem>
                                    <MenuItem value={FrequencyUnitTime.ANOS}>{t('accruals.grants.dialog.fields.unit_options.YEARS')}</MenuItem>
                                    <MenuItem value={FrequencyUnitTime.PERIODO_PAGO}>{t('accruals.grants.dialog.fields.unit_options.PERIOD_PAY')}</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Grid>
            )}

            <QualifiersList
                qualifiers={formData.qualifiers}
                setQualifiers={(value) => setFormData({ ...formData, qualifiers: value })}
                paycodes={paycodes}
            />
        </Grid>
    );
};
